import {
  doc,
  getDoc,
  setDoc,
  deleteDoc,
  serverTimestamp,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../firebase/firebase-config";

// `draftHangout/{userId}` holds one in-progress event per user — mirroring
// the `draftVenue/{userId}` pattern. A draft is auto-saved as the host
// fills out the Create Event wizard, restored the next time they open
// the modal, and deleted once the event is successfully created.

const draftDocRef = (userId) => doc(db, "draftHangout", userId);

/**
 * Persist the current state of the Create Event wizard for `userId`.
 * Uses `merge: true` so intermediate saves never clobber the whole doc.
 */
export const saveEventDraft = async (userId, formData, completedSections) => {
  if (!userId) return;
  try {
    await setDoc(
      draftDocRef(userId),
      {
        formData,
        completedSections,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
  } catch (e) {
    console.error("Event draft save failed:", e);
  }
};

/**
 * Fetch the stored draft for `userId`, or `null` if none exists.
 * Returns `{ formData, completedSections }` on success.
 */
export const loadEventDraft = async (userId) => {
  if (!userId) return null;
  try {
    const snap = await getDoc(draftDocRef(userId));
    if (!snap.exists()) return null;
    const { formData, completedSections } = snap.data();
    return {
      formData: formData || {},
      completedSections: Array.isArray(completedSections) ? completedSections : [],
    };
  } catch (e) {
    console.error("Event draft load failed:", e);
    return null;
  }
};

/**
 * Remove the stored draft for `userId` (called after a successful submit).
 */
export const deleteEventDraft = async (userId) => {
  if (!userId) return;
  try {
    await deleteDoc(draftDocRef(userId));
  } catch (e) {
    console.error("Event draft delete failed:", e);
  }
};

/**
 * Ask the backend to accept / reject a venue request on behalf of the
 * venue owner. `action` must be `"approved"` or `"rejected"`.
 *
 * Returns `{ success, eventId, status }` on success; throws on failure
 * so the UI can show a toast.
 */
export const respondToVenueRequest = async ({ eventId, action }) => {
  const fn = httpsCallable(functions, "respondToVenueRequest");
  const res = await fn({ eventId, action });
  return res.data;
};
